import { Section, Text } from "@react-email/components";
import useThemeContextualColors, {
  sectionToParent,
} from "../../hooks/use-theme-contextual-colors";
import { tLayout } from "../../translations";
import formatTemplate from "../../lib/format-template";
import { useReturnVariableValues } from "../../hooks/use-return-variable-values";
import {
  preserveBlankLines,
  resolveInstructionKey,
  resolveOverride,
} from "../return-shipping-instructions/instructions";
import { getTitleDefault, TITLE_FONT_SIZE_PX } from "./title-defaults";
import type { ReturnShippingInstructionsTitleBlockProps } from "./types";

const TITLE_STYLE = {
  fontWeight: "bold",
  fontSize: `${TITLE_FONT_SIZE_PX}px`,
} as const;

export function ReturnShippingInstructionsTitleEmailRenderer({
  block,
  formatter,
  theme,
  section,
  locale,
  defaultLocale,
}: ReturnShippingInstructionsTitleBlockProps) {
  const colors = useThemeContextualColors(theme, sectionToParent(section));
  const t = tLayout(locale, defaultLocale);
  const { getShippingInstructions } = useReturnVariableValues();
  const { carrierCode, hasLogisticLabel } = getShippingInstructions();

  const activeKey = resolveInstructionKey(carrierCode, hasLogisticLabel);

  // Merchant override (rich HTML, per locale) takes precedence; keyed by the raw
  // carrier code with a legacy-key fallback, an emptied editor ("<p></p>") is
  // treated as "no override" → default title.
  const override = resolveOverride(
    block.content,
    locale,
    defaultLocale,
    carrierCode,
    hasLogisticLabel,
  );

  if (override) {
    // The override HTML carries its own formatting (size/weight/color chosen in
    // the editor); only force the block-level color + centering, never the
    // default bold/18px, so the editor's parameters are respected.
    const withBlankLines = preserveBlankLines(override);
    return (
      <Section className="px-4">
        <div
          className="m-0 text-center"
          style={{ color: colors.text }}
          dangerouslySetInnerHTML={{
            __html: formatter
              ? formatter(formatTemplate(withBlankLines))
              : formatTemplate(withBlankLines),
          }}
        />
      </Section>
    );
  }

  // Default title from the block translations (tLayout has no per-key fallback).
  const def = getTitleDefault(activeKey);
  const translated = t(def.tLayoutKey);
  const text = translated === def.tLayoutKey ? def.fallback : translated;

  return (
    <Section className="px-4">
      <Text
        className="m-0 text-center"
        style={{ color: colors.text, ...TITLE_STYLE }}
      >
        {text}
      </Text>
    </Section>
  );
}
